import React, { useState, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  keyframes,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useOptimization } from '../../context/OptimizationContext';
import StatisticsView from '../StatisticsView/StatisticsView';

// Define keyframe animations
const flashBest = keyframes`
  0%, 100% {
    background-color: rgba(76, 175, 80, 0.1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
  }
  50% {
    background-color: rgba(76, 175, 80, 0.3);
    box-shadow: 0 0 8px 4px rgba(76, 175, 80, 0.6);
  }
`;

const flashWorst = keyframes`
  0%, 100% {
    background-color: rgba(211, 47, 47, 0.1);
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.4);
  }
  50% {
    background-color: rgba(211, 47, 47, 0.3);
    box-shadow: 0 0 8px 4px rgba(211, 47, 47, 0.6);
  }
`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`results-tabpanel-${index}`}
      aria-labelledby={`results-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ResultsView: React.FC = () => {
  const { results } = useOptimization();
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const algorithms = useMemo(() => ['GA', 'Culture', 'Frog', 'SFLAGA', 'PSO', 'GAPSO'], []);
  const algorithmKeys = useMemo(() => ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const, []);

  const hasResults = results !== null;

  // Generate analysis data for rendering
  const analysisData = useMemo(() => {
    if (!results) return null;

    // Fitness comparison
    const fitnessData = algorithmKeys.map(key => ({
      name: algorithms[algorithmKeys.indexOf(key)],
      fitness: results[key].fitness,
      key,
    })).sort((a, b) => b.fitness - a.fitness);

    // Energy comparison
    const energyData = algorithmKeys.map(key => ({
      name: algorithms[algorithmKeys.indexOf(key)],
      energy: results[key].energy,
      key,
    })).sort((a, b) => a.energy - b.energy);

    // Response Time comparison
    const responseTimeData = algorithmKeys.map(key => ({
      name: algorithms[algorithmKeys.indexOf(key)],
      responseTime: results[key].responseTime,
      key,
    })).sort((a, b) => a.responseTime - b.responseTime);

    return {
      fitnessData,
      energyData,
      responseTimeData,
    };
  }, [results, algorithms, algorithmKeys]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2">
          Results
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Solutions" />
          <Tab label="Fitness" />
          <Tab label="Configuration" />
          <Tab label="Energy" />
          <Tab label="Execution Cost" />
          <Tab label="Response Time" />
          <Tab label="Analysis" />
          {results && 'statistics' in results && results.statistics && (
            <Tab label="Statistics" />
          )}
        </Tabs>
      </Box>

      {/* Solutions Tab */}
      <TabPanel value={value} index={0}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            maxHeight: 400,
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              maxWidth: 200,
              wordBreak: 'break-all',
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell key={alg} align="center" sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>
                    {alg}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (
                <TableRow>
                  {algorithmKeys.map((key) => {
                    const solutionStr = results![key].solution.join(', ');
                    const truncated = solutionStr.length > 50 ? solutionStr.substring(0, 50) + '...' : solutionStr;
                    
                    return (
                      <TableCell 
                        key={key} 
                        align="center"
                        sx={{ 
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                        }}
                        title={solutionStr}
                      >
                        {truncated}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {hasResults && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            💡 Hover over solution values to see full content. Solutions show selected service indices for each task.
          </Typography>
        )}
      </TabPanel>

      {/* Fitness Tab */}
      <TabPanel value={value} index={1}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell key={alg} align="center" sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>
                    {alg}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (() => {
                // Calculate min and max fitness values
                const fitnessValues = algorithmKeys.map(key => results![key].fitness);
                const minFitness = Math.min(...fitnessValues);
                const maxFitness = Math.max(...fitnessValues);
                
                return (
                  <TableRow>
                    {algorithmKeys.map((key) => {
                      const fitness = results![key].fitness;
                      const isBest = fitness === maxFitness;
                      const isWorst = fitness === minFitness && maxFitness !== minFitness;
                      
                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontWeight: isBest || isWorst ? 600 : 400,
                            animation: isBest ? `${flashBest} 1.5s ease-in-out infinite` : isWorst ? `${flashWorst} 1.5s ease-in-out infinite` : 'none',
                            backgroundColor: isBest ? 'rgba(76, 175, 80, 0.1)' : isWorst ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                            borderRadius: isBest || isWorst ? '4px' : '0',
                            padding: isBest || isWorst ? '8px' : '16px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {fitness.toFixed(6)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })() : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Configuration Tab */}
      <TabPanel value={value} index={2}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ overflowX: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Task</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Property</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Initial Population</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Crossover</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>Mutation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (
                <TableRow>
                  <TableCell>{results!.configuration.taskCount}</TableCell>
                  <TableCell>{results!.configuration.propertyCount}</TableCell>
                  <TableCell>{results!.configuration.initialPopulation}</TableCell>
                  <TableCell>{results!.configuration.crossover}%</TableCell>
                  <TableCell>{results!.configuration.mutation}%</TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Energy Tab */}
      <TabPanel value={value} index={3}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell key={alg} align="center">
                     {alg} (J)
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (() => {
                // Calculate min and max energy values
                const energyValues = algorithmKeys.map(key => results![key].energy);
                const minEnergy = Math.min(...energyValues);
                const maxEnergy = Math.max(...energyValues);
                
                return (
                  <TableRow>
                    {algorithmKeys.map((key) => {
                      const energy = results![key].energy;
                      const isBest = energy === minEnergy; // Lower is better for energy
                      const isWorst = energy === maxEnergy && maxEnergy !== minEnergy;
                      
                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontWeight: isBest || isWorst ? 600 : 400,
                            animation: isBest ? `${flashBest} 1.5s ease-in-out infinite` : isWorst ? `${flashWorst} 1.5s ease-in-out infinite` : 'none',
                            backgroundColor: isBest ? 'rgba(76, 175, 80, 0.1)' : isWorst ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                            borderRadius: isBest || isWorst ? '4px' : '0',
                            padding: isBest || isWorst ? '8px' : '16px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {energy.toFixed(6)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })() : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Execution Cost Tab */}
      <TabPanel value={value} index={4}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell key={alg} align="center" sx={{ fontWeight: 600, backgroundColor: 'background.paper' }}>
                    {alg}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (() => {
                // Calculate min and max cost values
                const costValues = algorithmKeys.map(key => results![key].cost);
                const minCost = Math.min(...costValues);
                const maxCost = Math.max(...costValues);
                
                return (
                  <TableRow>
                    {algorithmKeys.map((key) => {
                      const cost = results![key].cost;
                      const isBest = cost === minCost; // Lower is better for cost
                      const isWorst = cost === maxCost && maxCost !== minCost;
                      
                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontWeight: isBest || isWorst ? 600 : 400,
                            animation: isBest ? `${flashBest} 1.5s ease-in-out infinite` : isWorst ? `${flashWorst} 1.5s ease-in-out infinite` : 'none',
                            backgroundColor: isBest ? 'rgba(76, 175, 80, 0.1)' : isWorst ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                            borderRadius: isBest || isWorst ? '4px' : '0',
                            padding: isBest || isWorst ? '8px' : '16px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {cost.toFixed(6)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })() : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Response Time Tab */}
      <TabPanel value={value} index={5}>
        <TableContainer 
          component={Paper} 
          variant="outlined"
          sx={{ 
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {algorithms.map((alg) => (
                  <TableCell key={alg} align="center">
                     {alg} (ms)
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {hasResults ? (() => {
                // Calculate min and max response time values
                const responseTimeValues = algorithmKeys.map(key => results![key].responseTime);
                const minResponseTime = Math.min(...responseTimeValues);
                const maxResponseTime = Math.max(...responseTimeValues);
                
                return (
                  <TableRow>
                    {algorithmKeys.map((key) => {
                      const responseTime = results![key].responseTime;
                      const isBest = responseTime === minResponseTime; // Lower is better for response time
                      const isWorst = responseTime === maxResponseTime && maxResponseTime !== minResponseTime;
                      
                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isBest ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontWeight: isBest || isWorst ? 600 : 400,
                            animation: isBest ? `${flashBest} 1.5s ease-in-out infinite` : isWorst ? `${flashWorst} 1.5s ease-in-out infinite` : 'none',
                            backgroundColor: isBest ? 'rgba(76, 175, 80, 0.1)' : isWorst ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                            borderRadius: isBest || isWorst ? '4px' : '0',
                            padding: isBest || isWorst ? '8px' : '16px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {responseTime.toFixed(2)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })() : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No results available. Please run optimization.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Analysis Tab */}
      <TabPanel value={value} index={6}>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            backgroundColor: '#fafafa',
            minHeight: 400,
            overflow: 'auto',
          }}
        >
          {!hasResults || !analysisData ? (
            <Typography variant="body2" color="text.secondary">
              No results available. Please run optimization.
            </Typography>
          ) : (
            <Box>
              {/* Header */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    mb: 1,
                    textTransform: 'uppercase',
                    letterSpacing: 2,
                  }}
                >
                  Analysis Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configuration: Tasks={results.configuration.taskCount}, Properties={results.configuration.propertyCount}, Population={results.configuration.initialPopulation}
                </Typography>
              </Box>

              {/* Fitness Comparison */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1976d2',
                    mb: 2,
                    pb: 1,
                    borderBottom: '2px solid #1976d2',
                  }}
                >
                  1. FITNESS COMPARISON (Higher is Better)
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Winner:
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      animation: `${flashBest} 1.5s ease-in-out infinite`,
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      border: '2px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'success.main',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                      }}
                    >
                      {analysisData.fitnessData[0].name} - {analysisData.fitnessData[0].fitness.toFixed(6)}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Ranking:
                  </Typography>
                  {analysisData.fitnessData.map((alg, idx) => {
                    const isWinner = idx === 0;
                    const isWorst = idx === analysisData.fitnessData.length - 1 && analysisData.fitnessData.length > 1;
                    return (
                      <Box
                        key={alg.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 1,
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: isWinner
                            ? 'rgba(76, 175, 80, 0.1)'
                            : isWorst
                            ? 'rgba(211, 47, 47, 0.1)'
                            : 'transparent',
                          animation: isWinner
                            ? `${flashBest} 1.5s ease-in-out infinite`
                            : isWorst
                            ? `${flashWorst} 1.5s ease-in-out infinite`
                            : 'none',
                          border: isWinner || isWorst ? '2px solid' : 'none',
                          borderColor: isWinner ? 'success.main' : isWorst ? 'error.main' : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            minWidth: 40,
                          }}
                        >
                          {idx + 1}.
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 700 : 500,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                            minWidth: 100,
                          }}
                        >
                          {alg.name}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 600 : 400,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                          }}
                        >
                          {alg.fitness.toFixed(6)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Energy Comparison */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#ed6c02',
                    mb: 2,
                    pb: 1,
                    borderBottom: '2px solid #ed6c02',
                  }}
                >
                  2. ENERGY CONSUMPTION COMPARISON (Lower is Better)
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Winner:
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      animation: `${flashBest} 1.5s ease-in-out infinite`,
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      border: '2px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'success.main',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                      }}
                    >
                      {analysisData.energyData[0].name} - {analysisData.energyData[0].energy.toFixed(6)} Joules
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Ranking:
                  </Typography>
                  {analysisData.energyData.map((alg, idx) => {
                    const isWinner = idx === 0;
                    const isWorst = idx === analysisData.energyData.length - 1 && analysisData.energyData.length > 1;
                    return (
                      <Box
                        key={alg.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 1,
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: isWinner
                            ? 'rgba(76, 175, 80, 0.1)'
                            : isWorst
                            ? 'rgba(211, 47, 47, 0.1)'
                            : 'transparent',
                          animation: isWinner
                            ? `${flashBest} 1.5s ease-in-out infinite`
                            : isWorst
                            ? `${flashWorst} 1.5s ease-in-out infinite`
                            : 'none',
                          border: isWinner || isWorst ? '2px solid' : 'none',
                          borderColor: isWinner ? 'success.main' : isWorst ? 'error.main' : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            minWidth: 40,
                          }}
                        >
                          {idx + 1}.
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 700 : 500,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                            minWidth: 100,
                          }}
                        >
                          {alg.name}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 600 : 400,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                          }}
                        >
                          {alg.energy.toFixed(6)} Joules
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Response Time Comparison */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#9c27b0',
                    mb: 2,
                    pb: 1,
                    borderBottom: '2px solid #9c27b0',
                  }}
                >
                  3. RESPONSE TIME COMPARISON (Lower is Better)
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Winner:
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      animation: `${flashBest} 1.5s ease-in-out infinite`,
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      border: '2px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'success.main',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                      }}
                    >
                      {analysisData.responseTimeData[0].name} - {analysisData.responseTimeData[0].responseTime.toFixed(2)} ms
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                    Ranking:
                  </Typography>
                  {analysisData.responseTimeData.map((alg, idx) => {
                    const isWinner = idx === 0;
                    const isWorst = idx === analysisData.responseTimeData.length - 1 && analysisData.responseTimeData.length > 1;
                    return (
                      <Box
                        key={alg.key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 1,
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: isWinner
                            ? 'rgba(76, 175, 80, 0.1)'
                            : isWorst
                            ? 'rgba(211, 47, 47, 0.1)'
                            : 'transparent',
                          animation: isWinner
                            ? `${flashBest} 1.5s ease-in-out infinite`
                            : isWorst
                            ? `${flashWorst} 1.5s ease-in-out infinite`
                            : 'none',
                          border: isWinner || isWorst ? '2px solid' : 'none',
                          borderColor: isWinner ? 'success.main' : isWorst ? 'error.main' : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            minWidth: 40,
                          }}
                        >
                          {idx + 1}.
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 700 : 500,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                            minWidth: 100,
                          }}
                        >
                          {alg.name}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: isWinner || isWorst ? 600 : 400,
                            color: isWinner ? 'success.main' : isWorst ? 'error.main' : 'text.primary',
                            fontFamily: 'monospace',
                          }}
                        >
                          {alg.responseTime.toFixed(2)} ms
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* Statistics Tab */}
      {results && 'statistics' in results && results.statistics && (
        <TabPanel value={value} index={7}>
          <StatisticsView results={results} />
        </TabPanel>
      )}
    </Box>
  );
};

export default ResultsView;
